/**
 * usePlayer: the course player's state (plan docs/plans/lms-plan.md 7.2, 7.3,
 * 8.1, 11.3, 18.15; WP-40). The stages, the slide walk and its bookmark, the
 * knowledge checks, the reading flags and the gates. The quiz's own screens
 * keep their state in QuizStage; the outcome they report (the completion)
 * lands here so the Done stage and the stepper see it.
 *
 * STAGES (7.2): overview, slides, reading, then quiz (or acknowledge, for a
 * read_ack course or an acknowledge-mode assignment, 8.1, 11.3), then done.
 *
 * WHAT CHANGED (7.2 "the change_summary when this is a retrain or acknowledge
 * assignment (shown first, as its own slide too)"; plan 18.27): the open
 * assignment's source is retrain or acknowledge, or its mode is acknowledge
 * whatever the source (a campaign sent under "acknowledge the change", such
 * as WP-37's sop_update). changeSummaryFor is that one rule; the overview,
 * the first slide and the acknowledge stage all read it from the hook.
 *
 * THE GATES (7.2, 18.15; the server enforces the same, this is presentation):
 *   - Reading may be opened at any time from the stepper;
 *   - the quiz waits for slides_done, and for reading_done when
 *     quiz_settings.require_reading is true;
 *   - the acknowledgement waits for reading_done (11.3; WP-34 refuses it
 *     otherwise).
 * slides_done is the server's (derived from slides_seen); the player also
 * counts it done once it has itself seen every authored slide, so the quiz
 * unlocks without waiting for the save to answer (the server re-checks at the
 * start of the attempt).
 *
 * THE BOOKMARK (5.5, 7.2): every change of slide saves {last_slide_id,
 * slides_seen}; answering a knowledge check saves too (the slide is only seen
 * once answered, so a check cannot be skipped by reloading). Opening the
 * reading stamps reading_opened once; "Mark as read" sets reading_done. A
 * failed save is shown (G10) and the next save sends the whole seen list
 * again, so nothing is lost.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiErrorMessage } from '../api/client';
import type { CompletionResult } from '../api/completions';
import { saveMyProgress } from '../api/progress';
import type { LmsProgress, ProgressSave } from '../api/progress';
import { buildDeck, canReach, furthestReachable } from '../components/player/deck';
import type { DeckResult } from '../components/player/deck';
import type { CourseData } from './useCourse';

export type PlayerStage = 'overview' | 'slides' | 'reading' | 'quiz' | 'acknowledge' | 'done';

/** quiz: the graded quiz (7.2); acknowledge: read and attest with the re-check (7.3, 8.1, 11.3). */
export type PlayerFlow = 'quiz' | 'acknowledge';

/** A knowledge check's or scenario's answer on this visit (never a record, 7.2). */
export interface SlideAnswer {
  selected: string[];
  checked: boolean;
}

export interface UsePlayerResult {
  deck: DeckResult;
  flow: PlayerFlow;
  /** The change summary shown first (changeSummaryFor), or null. */
  changeSummary: string | null;
  stage: PlayerStage;
  /** Move to a stage; refused (false) while it is locked. */
  goToStage: (stage: PlayerStage) => boolean;
  stageLock: (stage: PlayerStage) => string | null;

  index: number;
  goToSlide: (index: number) => boolean;
  next: () => boolean;
  prev: () => boolean;
  canNext: boolean;
  canPrev: boolean;
  canReachSlide: (index: number) => boolean;

  seen: ReadonlySet<string>;
  answers: Readonly<Record<string, SlideAnswer>>;
  isAnswered: (key: string) => boolean;
  selectOption: (slideKey: string, optionId: string, multi: boolean) => void;
  checkAnswer: (slideKey: string) => void;
  chooseScenario: (slideKey: string, optionId: string) => void;

  progress: LmsProgress | null;
  slidesDone: boolean;
  readingDone: boolean;
  requireReading: boolean;
  markRead: () => Promise<boolean>;
  saving: boolean;
  saveError: string | null;

  /** The section the reading view should scroll to (a review pointer), once. */
  readingTarget: string | null;
  reviewSection: (sectionId: string, where: 'reading' | 'slides') => void;
  clearReadingTarget: () => void;

  result: CompletionResult | null;
  finish: (result: CompletionResult) => void;
  hasStarted: boolean;
}

function union(a: Iterable<string>, b: Iterable<string>): Set<string> {
  const out = new Set<string>(a);
  Array.from(b).forEach((id) => out.add(id));
  return out;
}

/** The server's answer merged over what the player knows (never loses a seen slide or a done flag). */
function mergeProgress(prev: LmsProgress | null, saved: LmsProgress): LmsProgress {
  if (!prev || prev.course_version_id !== saved.course_version_id) return saved;
  return {
    ...saved,
    slides_seen: Array.from(union(prev.slides_seen || [], saved.slides_seen || [])),
    slides_done: prev.slides_done === true || saved.slides_done === true,
    reading_done: prev.reading_done === true || saved.reading_done === true,
    reading_opened_at: saved.reading_opened_at ?? prev.reading_opened_at ?? null,
  };
}

/**
 * The change summary the player shows first (7.2, plan 18.27): for an open
 * assignment whose source is retrain or acknowledge, or whose mode is
 * acknowledge; the assignment's pinned version's summary, else the course's.
 */
export function changeSummaryFor(data: Pick<CourseData, 'assignment' | 'course'>): string | null {
  const a = data.assignment;
  if (!a) return null;
  const shows = a.source === 'retrain' || a.source === 'acknowledge' || a.mode === 'acknowledge';
  return shows ? a.change_summary || data.course.change_summary || null : null;
}

export function usePlayer(courseId: string, data: CourseData): UsePlayerResult {
  const versionId = data.version.id;
  const changeSummary = changeSummaryFor(data);
  const deck = useMemo(() => buildDeck(data.content, { changeSummary }), [data.content, changeSummary]);
  const flow: PlayerFlow = data.course.kind === 'read_ack' || data.assignment?.mode === 'acknowledge' ? 'acknowledge' : 'quiz';
  // 7.2: the version's setting; when a version carries none, the server's default by category
  // (WP-34 effectiveQuizSettings over WP-15 defaultQuizSettings: compliance and clinical_skills read first).
  const requireReading =
    typeof data.quizSettings.require_reading === 'boolean'
      ? data.quizSettings.require_reading
      : data.course.kind !== 'read_ack' && (data.course.category === 'compliance' || data.course.category === 'clinical_skills');

  const authored = useMemo(() => new Set(deck.slideIds), [deck]);
  const [progress, setProgress] = useState<LmsProgress | null>(data.progress);
  const [seen, setSeenState] = useState<Set<string>>(
    () => new Set((data.progress?.slides_seen || []).filter((id) => authored.has(id)))
  );
  const seenRef = useRef(seen);
  const setSeen = useCallback((next: Set<string>) => {
    seenRef.current = next;
    setSeenState(next);
  }, []);

  const [answers, setAnswers] = useState<Record<string, SlideAnswer>>({});
  const answersRef = useRef(answers);
  answersRef.current = answers;

  const isAnswered = useCallback(
    (key: string) => seen.has(key) || answers[key]?.checked === true,
    [seen, answers]
  );

  const [index, setIndex] = useState<number>(() => {
    const last = data.progress?.last_slide_id;
    const at = last ? deck.slides.findIndex((s) => s.key === last) : -1;
    const answered = (key: string) => seen.has(key);
    const furthest = furthestReachable(deck.slides, answered);
    if (at < 0) return 0;
    return Math.min(at, furthest);
  });
  const indexRef = useRef(index);
  indexRef.current = index;

  const [stage, setStage] = useState<PlayerStage>('overview');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [readingTarget, setReadingTarget] = useState<string | null>(null);
  const [result, setResult] = useState<CompletionResult | null>(null);
  const openedRef = useRef<boolean>(Boolean(data.progress?.reading_opened_at));

  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const pending = useRef(0);
  const persist = useCallback(
    async (patch: Omit<ProgressSave, 'course_version_id'>): Promise<boolean> => {
      pending.current += 1;
      setSaving(true);
      try {
        const saved = await saveMyProgress(courseId, { course_version_id: versionId, ...patch });
        if (!mounted.current) return true;
        if (saved && saved.course_version_id === versionId) {
          setProgress((prev) => mergeProgress(prev, saved));
          const known = (saved.slides_seen || []).filter((id) => authored.has(id));
          if (known.some((id) => !seenRef.current.has(id))) setSeen(union(seenRef.current, known));
        }
        setSaveError(null);
        return true;
      } catch (err) {
        if (mounted.current) {
          setSaveError(apiErrorMessage(err, 'Your place could not be saved. The next step will try again.'));
        }
        return false;
      } finally {
        pending.current -= 1;
        if (mounted.current && pending.current === 0) setSaving(false);
      }
    },
    [courseId, versionId, authored, setSeen]
  );

  const seenList = useCallback((set: ReadonlySet<string>) => deck.slideIds.filter((id) => set.has(id)), [deck]);

  /** Show slide i: mark it seen (unless it is a check still to answer) and save the bookmark. */
  const visit = useCallback(
    (i: number) => {
      const entry = deck.slides[i];
      if (!entry) return;
      setIndex(i);
      indexRef.current = i;
      if (!entry.persisted) return;
      let next: Set<string> = seenRef.current;
      if (!entry.blocking && !next.has(entry.key)) {
        next = union(next, [entry.key]);
        setSeen(next);
      }
      void persist({ last_slide_id: entry.key, slides_seen: seenList(next) });
    },
    [deck, persist, seenList, setSeen]
  );

  const canReachSlide = useCallback((i: number) => canReach(deck.slides, i, isAnswered), [deck, isAnswered]);

  const goToSlide = useCallback(
    (i: number) => {
      if (!canReachSlide(i)) return false;
      if (i !== indexRef.current || stage !== 'slides') {
        setStage('slides');
        visit(i);
      }
      return true;
    },
    [canReachSlide, stage, visit]
  );

  const current = deck.slides[index];
  const canNext = index < deck.slides.length - 1 && !(current?.blocking && !isAnswered(current.key));
  const canPrev = index > 0;

  const next = useCallback(() => {
    if (!canNext) return false;
    visit(indexRef.current + 1);
    return true;
  }, [canNext, visit]);

  const prev = useCallback(() => {
    if (!canPrev) return false;
    visit(indexRef.current - 1);
    return true;
  }, [canPrev, visit]);

  const selectOption = useCallback((slideKey: string, optionId: string, multi: boolean) => {
    setAnswers((prevAnswers) => {
      const cur = prevAnswers[slideKey] || { selected: [], checked: false };
      if (cur.checked) return prevAnswers;
      const selected = multi
        ? cur.selected.includes(optionId)
          ? cur.selected.filter((id) => id !== optionId)
          : [...cur.selected, optionId]
        : [optionId];
      return { ...prevAnswers, [slideKey]: { selected, checked: false } };
    });
  }, []);

  const checkAnswer = useCallback(
    (slideKey: string) => {
      const cur = answersRef.current[slideKey];
      if (!cur || cur.checked || cur.selected.length === 0) return;
      const nextAnswers = { ...answersRef.current, [slideKey]: { ...cur, checked: true } };
      answersRef.current = nextAnswers;
      setAnswers(nextAnswers);
      if (!authored.has(slideKey)) return;
      const nextSeen = seenRef.current.has(slideKey) ? seenRef.current : union(seenRef.current, [slideKey]);
      if (nextSeen !== seenRef.current) setSeen(nextSeen);
      void persist({ last_slide_id: slideKey, slides_seen: seenList(nextSeen) });
    },
    [authored, persist, seenList, setSeen]
  );

  const chooseScenario = useCallback((slideKey: string, optionId: string) => {
    setAnswers((prevAnswers) => {
      if (prevAnswers[slideKey]?.checked) return prevAnswers;
      return { ...prevAnswers, [slideKey]: { selected: [optionId], checked: true } };
    });
  }, []);

  const slidesDone =
    progress?.slides_done === true || (deck.slideIds.length > 0 && deck.slideIds.every((id) => seen.has(id)));
  const readingDone = progress?.reading_done === true;

  const stageLock = useCallback(
    (s: PlayerStage): string | null => {
      switch (s) {
        case 'quiz':
          if (flow !== 'quiz') return 'This course has no quiz.';
          if (!slidesDone) return 'Finish every slide first.';
          if (requireReading && !readingDone) return 'Read the full document and mark it as read first.';
          return null;
        case 'acknowledge':
          if (flow !== 'acknowledge') return 'This course is completed with its quiz.';
          if (!readingDone) return 'Open the reading and mark it as read first.';
          return null;
        case 'done':
          return result ? null : 'Finish the course first.';
        default:
          return null;
      }
    },
    [flow, slidesDone, requireReading, readingDone, result]
  );

  const goToStage = useCallback(
    (s: PlayerStage) => {
      if (stageLock(s)) return false;
      if (s === 'slides') {
        setStage('slides');
        visit(indexRef.current);
        return true;
      }
      setStage(s);
      if (s === 'reading' && !openedRef.current) {
        openedRef.current = true;
        void persist({ reading_opened: true });
      }
      return true;
    },
    [stageLock, visit, persist]
  );

  const markRead = useCallback(async () => {
    const ok = await persist({ reading_done: true });
    if (ok && mounted.current) {
      // The server keeps reading_done once set (18.15); reflect it even if the answer was partial.
      setProgress((prevProgress) =>
        prevProgress
          ? { ...prevProgress, reading_done: true }
          : { course_version_id: versionId, last_slide_id: null, slides_seen: [], slides_done: false, reading_done: true }
      );
    }
    return ok;
  }, [persist, versionId]);

  const reviewSection = useCallback(
    (sectionId: string, where: 'reading' | 'slides') => {
      if (where === 'slides') {
        const section = deck.sections.find((s) => s.id === sectionId);
        if (section) goToSlide(section.start);
        return;
      }
      setReadingTarget(sectionId);
      goToStage('reading');
    },
    [deck, goToSlide, goToStage]
  );

  const clearReadingTarget = useCallback(() => setReadingTarget(null), []);

  const finish = useCallback((r: CompletionResult) => {
    setResult(r);
    setStage('done');
  }, []);

  const hasStarted = Boolean(progress && (progress.last_slide_id || (progress.slides_seen || []).length > 0));

  return {
    deck,
    flow,
    changeSummary,
    stage,
    goToStage,
    stageLock,
    index,
    goToSlide,
    next,
    prev,
    canNext,
    canPrev,
    canReachSlide,
    seen,
    answers,
    isAnswered,
    selectOption,
    checkAnswer,
    chooseScenario,
    progress,
    slidesDone,
    readingDone,
    requireReading,
    markRead,
    saving,
    saveError,
    readingTarget,
    reviewSection,
    clearReadingTarget,
    result,
    finish,
    hasStarted,
  };
}

export default usePlayer;
